import React, { useEffect, useState } from 'react';
import { Col, Form, Row } from 'antd';
import type { FormInstance } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { Card, Input, Select, Button, BUTTON_TYPES, RangePicker, notification, NOTIFICATION_TYPES } from '_atoms';
import { Typography, TYPOGRAPHY_WEIGHT } from '_atoms/Typography';
import { SELECT_TYPES } from '_atoms/Select/constants';
import { getCandidateSources } from 'store/selectors/candidateSelectors';
import { getCandidateSourcesAPI } from 'actions';
import { ACTIONS, Can, UI, useAbility } from 'permission';
import { fetchWrapper } from 'helpers/helpers';
import { useDebounceEffect } from 'helpers/useDebounceEffect';
import { ReactComponent as Arrow } from 'dist/icons/Vector1.svg';
import { ALL_LIMIT } from 'constants/constants';
import type { SelectOption } from '_atoms/Select/types';
import type { PaginatedResponse } from 'store/reducers/types';
import { CANDIDATE_STATUSES, STEPS_OPTIONS, OPTIONS_REQUEST_CONFIG, EXPERIENCE_LEVEL } from './constants';
import { optionsMappers } from './utils';
import './style.scss';

interface FiltersCandidateProps {
  form: FormInstance;
  initialValues: Record<string, unknown>;
  onFinish: (values: Record<string, unknown>) => void;
  onReset?: () => void;
  loading?: boolean;
}

const FiltersCandidate: React.FC<FiltersCandidateProps> = ({ form, initialValues, onFinish, onReset, loading }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const ability = useAbility();
  const showMoreAccess = ability.can(ACTIONS.READ, UI.CANDIDATE_LIST_DISPLAY_SHOW_MORE);
  const accessToSources = ability.can(ACTIONS.READ, UI.CANDIDATE_LIST_SOURCE);
  const [showExtraFilters, setShowExtraFilters] = useState(!showMoreAccess);
  const [filtersLoading, setFiltersLoading] = useState<Record<string, boolean>>({});
  const [filterOptions, setFilterOptions] = useState<Record<string, SelectOption[]>>({});
  const [openDetails, onOpenDetails] = useState(false);

  const sourcesOptions = useSelector(getCandidateSources);

  const onShowMore = () => setShowExtraFilters(s => !s);
  const onSearchDebounce = (type: string, value = '') => {
    if (filtersLoading[type]) return;
    setFiltersLoading(f => ({ ...f, [type]: true }));
    fetchWrapper<PaginatedResponse<Record<string, unknown>>>(OPTIONS_REQUEST_CONFIG[type](value))
      .then(options => {
        setFilterOptions(o => ({ ...o, [type]: optionsMappers[type](options.results, value) }));
        setFiltersLoading(f => ({ ...f, [type]: false }));
      })
      .catch(() => {
        setFilterOptions(o => ({ ...o, [type]: [] }));
        setFiltersLoading(f => ({ ...f, [type]: false }));
      });
  };
  const onSearch = useDebounceEffect(onSearchDebounce, 300);

  const fetchAll = (type: string) => () =>
    fetchWrapper<PaginatedResponse<Record<string, unknown>>>(OPTIONS_REQUEST_CONFIG[type]('', ALL_LIMIT))
      .then(options => {
        const mapped = optionsMappers[type](options.results, '');
        setFilterOptions(o => ({ ...o, [type]: mapped }));
        return mapped;
      })
      .catch(() => {
        notification({ type: NOTIFICATION_TYPES.ERROR, message: t('translation:bulk_select.load_all_error') });
        return [];
      });

  const onBeforeFinish = values => {
    onFinish({ ...initialValues, ...values });
  };

  const typeToSearch = () => onBeforeFinish(form.getFieldsValue());

  const onChangeCountry = countries => {
    form.setFieldValue('city', []);
    const citiesOptions = countries.map(c => optionsMappers.city(c.cities)).flat();
    setFilterOptions(o => ({ ...o, city: citiesOptions }));
  };

  useEffect(() => {
    if (accessToSources) dispatch(getCandidateSourcesAPI());
  }, [accessToSources]);

  return (
    <Card className={cn('candidates-filters', { open: openDetails })}>
      <div className="candidates-filters__header">
        <Typography.Title2 weight={TYPOGRAPHY_WEIGHT.BOLD}>
          {t('translation:filters_candidate.title')}
        </Typography.Title2>
        <Button
          className={cn('open-details original-svg', { open: openDetails })}
          onClick={() => onOpenDetails(o => !o)}
          type={BUTTON_TYPES.GHOST}
          icon={<Arrow />}
        />
      </div>
      <Form form={form} onFinish={onBeforeFinish} initialValues={initialValues}>
        <Input
          placeholder={t('translation:filters_candidate.ph_search_by_name')}
          type="search"
          onDebounceChange={typeToSearch}
          itemProps={{ name: 'search' }}
          className="filter-candidate-name"
        />
        <div className="candidates-filters__body">
          <Row gutter={[32, 0]}>
            <Can I={ACTIONS.READ} a={UI.CANDIDATE_LIST_SERVICE} passThrough>
              {access => (
                <Col span={24} lg={24} md={12} sm={24} className={cn({ hidden_field: !access })}>
                  <Select
                    labelInValue
                    label={t('translation:filters_candidate.label_service')}
                    placeholder={t('translation:filters_candidate.ph_service')}
                    options={filterOptions.service}
                    loading={filtersLoading.service}
                    onSearch={v => onSearch('service', v)}
                    onDropdownVisibleChange={open => open && onSearchDebounce('service')}
                    fetchAllOptions={fetchAll('service')}
                    mode={SELECT_TYPES.MULTIPLE}
                    filterOption={false}
                    itemProps={{ name: 'service' }}
                  />
                </Col>
              )}
            </Can>
            <Can I={ACTIONS.READ} a={UI.CANDIDATE_LIST_TEAM_LEAD} passThrough>
              {access => (
                <Col span={24} lg={24} md={12} sm={24} className={cn({ hidden_field: !access })}>
                  <Select
                    labelInValue
                    label={t('translation:filters_candidate.label_team_lead')}
                    placeholder={t('translation:filters_candidate.ph_team_lead')}
                    options={filterOptions.team_lead}
                    loading={filtersLoading.team_lead}
                    onSearch={v => onSearch('team_lead', v)}
                    onDropdownVisibleChange={open => open && onSearchDebounce('team_lead')}
                    fetchAllOptions={fetchAll('team_lead')}
                    mode={SELECT_TYPES.MULTIPLE}
                    filterOption={false}
                    itemProps={{ name: 'team_lead' }}
                  />
                </Col>
              )}
            </Can>
            <Can I={ACTIONS.READ} a={UI.CANDIDATE_LIST_JOB} passThrough>
              {access => (
                <Col span={24} lg={24} md={12} sm={24} className={cn({ hidden_field: !access })}>
                  <Select
                    labelInValue
                    label={t('translation:filters_candidate.label_job')}
                    placeholder={t('translation:filters_candidate.ph_job')}
                    options={filterOptions.jobs}
                    loading={filtersLoading.jobs}
                    onSearch={v => onSearch('jobs', v)}
                    onDropdownVisibleChange={open => open && onSearchDebounce('jobs')}
                    fetchAllOptions={fetchAll('jobs')}
                    mode={SELECT_TYPES.MULTIPLE}
                    filterOption={false}
                    itemProps={{ name: 'jobs' }}
                  />
                </Col>
              )}
            </Can>
            <Can I={ACTIONS.READ} a={UI.CANDIDATE_LIST_JOB_STEP} passThrough>
              {access => (
                <Col span={24} lg={24} md={12} sm={24} className={cn({ hidden_field: !access })}>
                  <Select
                    labelInValue
                    label={t('translation:filters_candidate.label_job_step')}
                    placeholder={t('translation:filters_candidate.ph_job_step')}
                    options={STEPS_OPTIONS}
                    mode={SELECT_TYPES.MULTIPLE}
                    itemProps={{ name: 'job_step' }}
                  />
                </Col>
              )}
            </Can>
            <Can I={ACTIONS.READ} a={UI.CANDIDATE_LIST_CANDIDATE_STATUS} passThrough>
              {access => (
                <Col span={24} lg={24} md={12} sm={24} className={cn({ hidden_field: !access })}>
                  <Select
                    labelInValue
                    label={t('translation:filters_candidate.label_candidate_status')}
                    placeholder={t('translation:filters_candidate.ph_candidate_status')}
                    options={CANDIDATE_STATUSES}
                    mode={SELECT_TYPES.MULTIPLE}
                    itemProps={{ name: 'status' }}
                  />
                </Col>
              )}
            </Can>
            <Can I={ACTIONS.READ} a={UI.CANDIDATE_LIST_SPECIALTY} passThrough>
              {access => (
                <Col span={24} lg={24} md={12} sm={24} className={cn({ hidden_field: !access })}>
                  <Select
                    labelInValue
                    label={t('translation:filters_candidate.label_speciality')}
                    placeholder={t('translation:filters_candidate.ph_speciality')}
                    options={filterOptions.speciality}
                    loading={filtersLoading.speciality}
                    onSearch={v => onSearch('speciality', v)}
                    onDropdownVisibleChange={open => open && onSearchDebounce('speciality')}
                    fetchAllOptions={fetchAll('speciality')}
                    mode={SELECT_TYPES.MULTIPLE}
                    filterOption={false}
                    itemProps={{ name: 'speciality' }}
                  />
                </Col>
              )}
            </Can>
            <Can I={ACTIONS.READ} a={UI.CANDIDATE_LIST_TAGS} passThrough>
              {access => (
                <Col span={24} lg={24} md={12} sm={24} className={cn({ hidden_field: !access })}>
                  <Select
                    labelInValue
                    label={t('translation:filters_candidate.label_tags')}
                    placeholder={t('translation:filters_candidate.ph_tags')}
                    options={filterOptions.tags}
                    loading={filtersLoading.tags}
                    onSearch={v => onSearch('tags', v)}
                    onDropdownVisibleChange={open => open && onSearchDebounce('tags')}
                    fetchAllOptions={fetchAll('tags')}
                    mode={SELECT_TYPES.MULTIPLE}
                    filterOption={false}
                    itemProps={{ name: 'tags' }}
                  />
                </Col>
              )}
            </Can>
            <Can I={ACTIONS.READ} a={UI.CANDIDATE_LIST_COMPANIES} passThrough>
              {access => (
                <Col span={24} lg={24} md={12} sm={24} className={cn({ hidden_field: !access || !showExtraFilters })}>
                  <Select
                    labelInValue
                    label={t('translation:filters_candidate.label_companies')}
                    placeholder={t('translation:filters_candidate.ph_companies')}
                    options={filterOptions.companies}
                    loading={filtersLoading.companies}
                    onSearch={v => onSearch('companies', v)}
                    onDropdownVisibleChange={open => open && onSearchDebounce('companies')}
                    fetchAllOptions={fetchAll('companies')}
                    mode={SELECT_TYPES.MULTIPLE}
                    filterOption={false}
                    itemProps={{ name: 'companies' }}
                  />
                </Col>
              )}
            </Can>
            <Can I={ACTIONS.READ} a={UI.CANDIDATE_LIST_COUNTRY} passThrough>
              {access => (
                <Col span={24} lg={24} md={12} sm={24} className={cn({ hidden_field: !access || !showExtraFilters })}>
                  <Select
                    labelInValue
                    label={t('translation:filters_candidate.label_country')}
                    placeholder={t('translation:filters_candidate.ph_country')}
                    options={filterOptions.country}
                    loading={filtersLoading.country}
                    onSearch={v => onSearch('country', v)}
                    onDropdownVisibleChange={open => open && onSearchDebounce('country')}
                    fetchAllOptions={fetchAll('country')}
                    mode={SELECT_TYPES.MULTIPLE}
                    filterOption={false}
                    itemProps={{ name: 'country' }}
                    onChange={onChangeCountry}
                  />
                </Col>
              )}
            </Can>
            <Can I={ACTIONS.READ} a={UI.CANDIDATE_LIST_CITY} passThrough>
              {access => (
                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.country !== curr.country}>
                  {({ getFieldValue }) =>
                    !!getFieldValue('country').length && (
                      <Col
                        span={24}
                        lg={24}
                        md={12}
                        sm={24}
                        className={cn({ hidden_field: !access || !showExtraFilters })}
                      >
                        <Select
                          labelInValue
                          label={t('translation:filters_candidate.label_city')}
                          placeholder={t('translation:filters_candidate.ph_city')}
                          options={filterOptions.city}
                          mode={SELECT_TYPES.MULTIPLE}
                          itemProps={{ name: 'city' }}
                        />
                      </Col>
                    )
                  }
                </Form.Item>
              )}
            </Can>
            <Can I={ACTIONS.READ} a={UI.CANDIDATE_LIST_RESPONSIBLE_RECRUITER} passThrough>
              {access => (
                <Col span={24} lg={24} md={12} sm={24} className={cn({ hidden_field: !access || !showExtraFilters })}>
                  <Select
                    labelInValue
                    label={t('translation:filters_candidate.label_responsible_recruiter')}
                    placeholder={t('translation:filters_candidate.ph_responsible_recruiter')}
                    options={filterOptions.responsible_recruiters}
                    loading={filtersLoading.responsible_recruiters}
                    onSearch={v => onSearch('responsible_recruiters', v)}
                    onDropdownVisibleChange={open => open && onSearchDebounce('responsible_recruiters')}
                    fetchAllOptions={fetchAll('responsible_recruiters')}
                    mode={SELECT_TYPES.MULTIPLE}
                    filterOption={false}
                    itemProps={{ name: 'responsible_recruiters' }}
                  />
                </Col>
              )}
            </Can>
            <Can I={ACTIONS.READ} a={UI.CANDIDATE_LIST_INTERACTED_BY} passThrough>
              {access => (
                <Col span={24} lg={24} md={12} sm={24} className={cn({ hidden_field: !access || !showExtraFilters })}>
                  <Select
                    labelInValue
                    label={t('translation:filters_candidate.label_interacted_by')}
                    placeholder={t('translation:filters_candidate.ph_interacted_by')}
                    options={filterOptions.interacted_by}
                    loading={filtersLoading.interacted_by}
                    onSearch={v => onSearch('interacted_by', v)}
                    onDropdownVisibleChange={open => open && onSearchDebounce('interacted_by')}
                    fetchAllOptions={fetchAll('interacted_by')}
                    mode={SELECT_TYPES.MULTIPLE}
                    filterOption={false}
                    itemProps={{ name: 'interacted_by' }}
                  />
                </Col>
              )}
            </Can>
            <Can I={ACTIONS.READ} a={UI.CANDIDATE_LIST_SOURCE} passThrough>
              {access => (
                <Col span={24} lg={24} md={12} sm={24} className={cn({ hidden_field: !access || !showExtraFilters })}>
                  <Select
                    labelInValue
                    label={t('translation:filters_candidate.label_source')}
                    placeholder={t('translation:filters_candidate.ph_sources')}
                    options={sourcesOptions}
                    mode={SELECT_TYPES.MULTIPLE}
                    itemProps={{ name: 'sources' }}
                  />
                </Col>
              )}
            </Can>
            <Can I={ACTIONS.READ} a={UI.CANDIDATE_LIST_LAST_INTERACTION} passThrough>
              {access => (
                <Col span={24} lg={24} md={12} sm={24} className={cn({ hidden_field: !access || !showExtraFilters })}>
                  <RangePicker
                    label={t('translation:filters_candidate.label_last_interaction')}
                    itemProps={{ name: 'last_interaction' }}
                  />
                </Col>
              )}
            </Can>
            <Can I={ACTIONS.READ} a={UI.CANDIDATE_LIST_INTERVIEWED_BY} passThrough>
              {access => (
                <Col span={24} lg={24} md={12} sm={24} className={cn({ hidden_field: !access || !showExtraFilters })}>
                  <Select
                    labelInValue
                    label={t('translation:filters_candidate.label_interviewed_by')}
                    placeholder={t('translation:filters_candidate.ph_interviewed_by')}
                    options={filterOptions.interviewed_by}
                    loading={filtersLoading.interviewed_by}
                    onSearch={v => onSearch('interviewed_by', v)}
                    onDropdownVisibleChange={open => open && onSearchDebounce('interviewed_by')}
                    fetchAllOptions={fetchAll('interviewed_by')}
                    mode={SELECT_TYPES.MULTIPLE}
                    filterOption={false}
                    itemProps={{ name: 'interviewed_by' }}
                  />
                </Col>
              )}
            </Can>
            <Can I={ACTIONS.READ} a={UI.CANDIDATE_LIST_INTERVIEWED_DATE} passThrough>
              {access => (
                <Col span={24} lg={24} md={12} sm={24} className={cn({ hidden_field: !access || !showExtraFilters })}>
                  <RangePicker
                    label={t('translation:filters_candidate.label_interview_date')}
                    itemProps={{ name: 'interview_date' }}
                  />
                </Col>
              )}
            </Can>
            <Can I={ACTIONS.READ} a={UI.CANDIDATE_LIST_EXPERIENCE_LEVEL} passThrough>
              {access => (
                <Col span={24} lg={24} md={12} sm={24} className={cn({ hidden_field: !access })}>
                  <Select
                    labelInValue
                    label={t('translation:filters_candidate.label_experience_level')}
                    placeholder={t('translation:filters_candidate.ph_experience_level')}
                    options={EXPERIENCE_LEVEL.slice(0, -1)}
                    mode={SELECT_TYPES.MULTIPLE}
                    itemProps={{ name: 'experience_level' }}
                  />
                </Col>
              )}
            </Can>
            <Can I={ACTIONS.READ} a={UI.CANDIDATE_LIST_ADDED_BY} passThrough>
              {access => (
                <Col span={24} lg={24} md={12} sm={24} className={cn({ hidden_field: !access || !showExtraFilters })}>
                  <Select
                    labelInValue
                    label={t('translation:filters_candidate.label_added_by')}
                    placeholder={t('translation:filters_candidate.ph_added_by')}
                    options={filterOptions.added_by}
                    loading={filtersLoading.added_by}
                    onSearch={v => onSearch('added_by', v)}
                    onDropdownVisibleChange={open => open && onSearchDebounce('added_by')}
                    fetchAllOptions={fetchAll('added_by')}
                    mode={SELECT_TYPES.MULTIPLE}
                    filterOption={false}
                    itemProps={{ name: 'added_by' }}
                  />
                </Col>
              )}
            </Can>
            <Can I={ACTIONS.READ} a={UI.CANDIDATE_LIST_ADDED_DURING_PERIOD} passThrough>
              {access => (
                <Col span={24} lg={24} md={12} sm={24} className={cn({ hidden_field: !access || !showExtraFilters })}>
                  <RangePicker
                    label={t('translation:filters_candidate.label_added_during')}
                    itemProps={{ name: 'added_during_date' }}
                  />
                </Col>
              )}
            </Can>
          </Row>
          <Can I={ACTIONS.READ} a={UI.CANDIDATE_LIST_DISPLAY_SHOW_MORE}>
            <Button type={BUTTON_TYPES.TEXT} className="candidates-filters__show-more" onClick={onShowMore}>
              {showExtraFilters
                ? t('translation:filters_candidate.button_show_less')
                : t('translation:filters_candidate.button_show_more')}
            </Button>
          </Can>
          <div className="candidates-filters__footer">
            <Button type={BUTTON_TYPES.SECONDARY} onClick={loading ? undefined : onReset}>
              {t('translation:filters_candidate.button_reset_filters')}
            </Button>
            <Button type={BUTTON_TYPES.PRIMARY} htmlType={loading ? 'button' : 'submit'}>
              {t('translation:filters_candidate.button_apply')}
            </Button>
          </div>
        </div>
      </Form>
    </Card>
  );
};

export default FiltersCandidate;
